// End-to-end encryption for the synced copy of the user's data.
//
// How it works
//  - The user picks a passphrase that is never sent anywhere. A 256-bit key is derived from it in the browser with
//    PBKDF2-SHA256 (600,000 rounds, random 16-byte salt).
//  - The whole { profiles, activeId } blob is encrypted with AES-256-GCM (fresh random 96-bit IV every time, authenticated)
//    before it is uploaded. The server stores only the envelope below; it holds no key and cannot read the data.
//  - Any tampering with the stored ciphertext, or a wrong passphrase, makes decryption fail (GCM authentication).
//  - The derived key is non-extractable. It is kept in memory, and (if "remember on this device" is on) in IndexedDB so the
//    passphrase isn't asked for on every launch of that device.
//  - There is NO recovery. A forgotten passphrase means the online copy can't be read. Local data and backups are unaffected.

const enc = new TextEncoder();
const dec = new TextDecoder();
const AAD = enc.encode("balance-beyond:sync:v1");   // binds ciphertext to this app and format version

export const KDF_ITERATIONS = 600000;
const MIN_ITER = 100000, MAX_ITER = 2000000;   // refuse absurd values supplied by a hostile server (denial of service)

export const b64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
};
export const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
export const randomSalt = () => b64(crypto.getRandomValues(new Uint8Array(16)));

export const isEnvelope = (d) => !!d && typeof d === "object" && d._e2ee === 1 && typeof d.ct === "string" && typeof d.iv === "string" && typeof d.salt === "string";

export function passphraseProblem(p) {
  const s = String(p || "");
  if (s.length < 10) return "Use at least 10 characters. Four random words works well.";
  if (new Set(s).size < 5) return "That passphrase is too repetitive.";
  if (/^\d+$/.test(s)) return "Use more than digits.";
  return "";
}

export async function deriveKey(passphrase, saltB64, iterations = KDF_ITERATIONS) {
  if (!(iterations >= MIN_ITER && iterations <= MAX_ITER)) throw Object.assign(new Error("Unsupported encryption settings."), { code: "bad-params" });
  const base = await crypto.subtle.importKey("raw", enc.encode(String(passphrase).normalize("NFKC")), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: unb64(saltB64), iterations, hash: "SHA-256" },
    base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"],
  );
}

export async function encryptJson(key, value, { salt, iterations }) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: AAD }, key, enc.encode(JSON.stringify(value)));
  return { _e2ee: 1, v: 1, kdf: "PBKDF2-SHA256", iter: iterations, salt, iv: b64(iv), ct: b64(ct) };
}

export async function decryptJson(key, envelope) {
  try {
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(envelope.iv), additionalData: AAD }, key, unb64(envelope.ct));
    return JSON.parse(dec.decode(pt));
  } catch (e) {
    throw Object.assign(new Error("That passphrase doesn't match."), { code: "bad-passphrase" });
  }
}

// ---- where the derived key lives -------------------------------------------------------------------------------
const mem = new Map();   // userId -> { key, salt, iterations }
const DB = "bb-keys", STORE = "keys";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
const idbDo = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(req?.result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
};

export async function getKeyRecord(userId) {
  if (mem.has(userId)) return mem.get(userId);
  try {
    const rec = await idbDo("readonly", (s) => s.get(userId));
    if (rec?.key) { mem.set(userId, rec); return rec; }
  } catch (e) { /* storage unavailable: treat as locked */ }
  return null;
}
export async function setKeyRecord(userId, rec, remember = true) {
  mem.set(userId, rec);
  if (remember) { try { await idbDo("readwrite", (s) => s.put(rec, userId)); } catch (e) { /* stays in memory only */ } }
}
export async function deleteKeyRecord(userId) {
  mem.delete(userId);
  try { await idbDo("readwrite", (s) => s.delete(userId)); } catch (e) { /* ignore */ }
}

// A per-user flag so a device remembers "this account is encrypted" even before it has the key.
const flagName = (userId) => `bb-e2ee:${userId}`;
export const hasEncryptionFlag = (userId) => { try { return localStorage.getItem(flagName(userId)) === "1"; } catch (e) { return false; } };
export const setEncryptionFlag = (userId, on) => { try { on ? localStorage.setItem(flagName(userId), "1") : localStorage.removeItem(flagName(userId)); } catch (e) { /* ignore */ } };
